import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  DndContext,
  TouchSensor,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Menu, HelpCircle, LogOut, Sliders, Trash2 } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { useUnreadBadges } from "@/hooks/useUnreadBadges";
import { useEffectiveNav } from "@/hooks/useEffectiveNav";
import { useLongPress } from "@/hooks/useLongPress";
import { openNavCustomizer } from "@/lib/nav-customizer";
import { buzz } from "@/lib/dar-prefs";
import { moveItem } from "@/lib/nav-prefs";
import { type NavItem } from "@/lib/nav-catalog";
import { toast } from "sonner";
import { DarNavMic } from "@/components/dar/DarNavMic";
import { usePrefetchOnIntent, usePrefetchScreens } from "@/lib/screen-prefetch";

const TRASH = "bottom-nav-trash";

/** Items that show a live unread badge. */
const BADGE_BY_ID: Record<string, "messages"> = { messages: "messages" };

function ItemInner({ item, active, count }: { item: NavItem; active: boolean; count: number }) {
  const Icon = item.icon;
  return (
    <>
      <span className="relative">
        <Icon className={cn("h-5 w-5 transition-transform", active && "stroke-[2.5] scale-110")} />
        {count > 0 && (
          <span
            className="absolute -right-2 -top-1 inline-flex min-w-[16px] items-center justify-center rounded-full bg-[#CC0000] px-1 text-[9px] font-semibold leading-4 text-white"
            aria-label={`${count} unread`}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </span>
      <span className="max-w-full truncate px-0.5">{item.label}</span>
    </>
  );
}

const cellClass =
  "relative flex h-14 min-h-11 flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#CC0000]";

/** Draggable, non-navigating version of a nav item while in edit mode. */
function EditableItem({ item, count }: { item: NavItem; count: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      aria-label={`Reorder ${item.label}`}
      className={cn(
        cellClass,
        "touch-none text-white",
        isDragging ? "z-40 scale-110 text-[#CC0000]" : "animate-pulse",
      )}
    >
      <ItemInner item={item} active={false} count={count} />
    </button>
  );
}

function TrashBar({ visible }: { visible: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: TRASH });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex items-center justify-center gap-2 border-t px-3 py-3 text-xs font-medium transition-all",
        visible ? "opacity-100" : "pointer-events-none h-0 overflow-hidden opacity-0 py-0",
        isOver
          ? "border-[#CC0000] bg-[#CC0000]/25 text-[#CC0000]"
          : "border-[#CC0000]/40 bg-[#CC0000]/10 text-[#CC0000]/90",
      )}
    >
      <Trash2 className="h-4 w-4" />
      Drag here to remove from your nav bar
    </div>
  );
}

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const { signOut } = useAuth();
  const badges = useUnreadBadges();
  const nav = useEffectiveNav();
  const { bottomItems, bottomIds, overflow } = nav;

  // Warm the data behind the pinned destinations (not just their code) so the
  // screen is already populated when the user taps.
  usePrefetchScreens(bottomItems.slice(0, 3).map((i) => i.route));
  const prefetchIntent = usePrefetchOnIntent();

  // Five physical positions: 3 configurable, fixed D.A.R. centre, fixed More.
  const configurable = bottomItems.slice(0, 3);
  const leading = configurable.slice(0, 2);
  const trailing = configurable.slice(2);

  const isActive = (to: string) =>
    location.pathname === to || location.pathname.startsWith(to + "/");

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    navigate({ to: "/" });
  };

  const enterEdit = () => {
    buzz(18);
    setEditing(true);
  };

  const longPress = useLongPress(enterEdit);

  const sensors = useSensors(
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const onDragEnd = (e: DragEndEvent) => {
    setDragging(false);
    const { active, over } = e;
    if (!over) return;
    const id = String(active.id);
    const overId = String(over.id);

    if (overId === TRASH) {
      const next = bottomIds.filter((x) => x !== id);
      if (next.length === 0) {
        toast.error("Keep at least one item in your nav bar.");
        return;
      }
      buzz(12);
      void nav.save({ bottom_nav_order: next });
      toast.success("Moved to your menu. The page is still available.");
      return;
    }
    if (overId !== id && bottomIds.includes(overId)) {
      void nav.save({
        bottom_nav_order: moveItem(bottomIds, bottomIds.indexOf(id), bottomIds.indexOf(overId)),
      });
    }
  };

  const renderItem = (item: NavItem) => {
    const active = isActive(item.route);
    const badgeKey = BADGE_BY_ID[item.id];
    const count = badgeKey ? (badges[badgeKey] ?? 0) : 0;
    return (
      <Link
        key={item.id}
        to={item.route as never}
        // Pinned nav destinations are the likeliest next screen: warm their
        // chunk + loader as soon as the bar renders, not on hover/tap.
        preload="render"
        aria-current={active ? "page" : undefined}
        aria-label={item.label}
        {...longPress.handlers}
        className={cn(cellClass, active ? "text-[#CC0000]" : "text-[#666666]")}
      >
        <ItemInner item={item} active={active} count={count} />
      </Link>
    );
  };

  if (bottomItems.length === 0) return null;

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-[#111111] md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        aria-label="Primary"
        data-dock-target="bottom-nav"
      >
        {editing ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) => {
              void e;
              setDragging(true);
              buzz(10);
            }}
            onDragEnd={onDragEnd}
            onDragCancel={() => setDragging(false)}
          >
            <TrashBar visible={dragging} />
            <div className="flex items-center justify-between border-b border-white/10 px-3 py-2 text-[11px] text-white/70">
              <span>Drag to reorder · drag to trash to unpin</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    openNavCustomizer();
                  }}
                  className="rounded-md border border-white/15 px-2 py-1 text-[11px] text-white"
                >
                  More options
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="rounded-md bg-[#CC0000] px-3 py-1 text-[11px] font-semibold text-white"
                >
                  Done
                </button>
              </div>
            </div>
            <SortableContext items={bottomIds} strategy={horizontalListSortingStrategy}>
              <div
                className="grid"
                style={{
                  gridTemplateColumns: `repeat(${Math.max(1, bottomItems.length)}, minmax(0, 1fr))`,
                }}
              >
                {configurable.map((item) => (
                  <EditableItem
                    key={item.id}
                    item={item}
                    count={BADGE_BY_ID[item.id] ? (badges[BADGE_BY_ID[item.id]!] ?? 0) : 0}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="grid grid-cols-5">
            {leading.map(renderItem)}
            {leading.length < 2 &&
              Array.from({ length: 2 - leading.length }, (_, i) => (
                <span key={`pad-lead-${i}`} aria-hidden className="h-14" />
              ))}

            {nav.darAuthorized ? <DarNavMic /> : <span aria-hidden className="h-14" />}

            {trailing.map(renderItem)}
            {trailing.length === 0 && <span aria-hidden className="h-14" />}

            {
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    aria-label="More navigation"
                    className={cn(cellClass, open ? "text-[#CC0000]" : "text-[#666666]")}
                  >
                    <Menu className={cn("h-5 w-5 transition-transform", open && "stroke-[2.5]")} />
                    <span>More</span>
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="bottom"
                  className="max-h-[85dvh] touch-pan-y overflow-y-auto overscroll-contain rounded-t-2xl border-white/10 bg-[#0D0D0D] text-white"
                  style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
                >
                  <SheetHeader className="pb-2">
                    <SheetTitle className="text-white">More</SheetTitle>
                  </SheetHeader>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      openNavCustomizer();
                    }}
                    className="mb-3 flex w-full items-center gap-3 rounded-lg border border-[#CC0000]/40 bg-[#CC0000]/10 px-3 py-3 text-sm font-medium text-[#CC0000] transition-colors hover:bg-[#CC0000]/20 min-h-11"
                  >
                    <Sliders className="h-4 w-4" />
                    <span className="truncate">Customize nav</span>
                  </button>
                  <div className="grid grid-cols-2 gap-2 pb-2">
                    {overflow.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.route);
                      return (
                        <Link
                          key={item.id}
                          to={item.route as never}
                          preload="viewport"
                          {...prefetchIntent(item.route)}
                          onClick={() => setOpen(false)}
                          className={cn(
                            "flex items-center gap-3 rounded-lg border px-3 py-3 text-sm transition-colors min-h-11",
                            active
                              ? "border-[#CC0000]/50 bg-[#CC0000]/10 text-[#CC0000]"
                              : "border-white/10 bg-white/5 text-white hover:bg-white/10",
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          <span className="truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                    <Link
                      to="/faq"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-sm text-white transition-colors hover:bg-white/10 min-h-11"
                    >
                      <HelpCircle className="h-4 w-4" />
                      <span className="truncate">Help Center</span>
                    </Link>
                  </div>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-[#CC0000]/40 bg-[#CC0000]/10 px-3 py-3 text-sm font-medium text-[#CC0000] transition-colors hover:bg-[#CC0000]/20 min-h-11"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </SheetContent>
              </Sheet>
            }
          </div>
        )}
      </nav>
      <div className="h-16 md:hidden" aria-hidden />
    </>
  );
}
